import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import {
    Container, Box, Paper, Typography, TableContainer, Chip, Popover,
    Table, TableHead, TableRow, TableCell, TableBody, Link, Alert, IconButton, Divider,
    Skeleton, Stack, Button
} from '@mui/material';
import { User } from 'firebase/auth';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TodayIcon from '@mui/icons-material/Today';
import { logEvent } from '../analytics';
import {
    getEventIcon,
    formatLocation,
    EventType,
    getEventType,
    getLiveBarnLink,
    formatEventTime,
    isMultiDayEvent,
    isAllDayEvent,
    formatEventDateRange,
    getLocalDateString,
    getShortenedEventTitle,
    getPlayerBirthdayEvents,
    isHomeGame,
    isAwayGame,
    convertTournamentGameToCalendarEvent,
    mergeTournamentAndCalendarEvents,
} from '../utils/eventUtils';
import { CalendarEvent } from '../types';
import ScheduleConflictDisplay from './ScheduleConflictDisplay';
import { fetchAndMergeCalendarData } from '../utils/dataUtils';
import { useRoster } from '../RosterContext';
import { CURRENT_SEASON_KEY } from '../seasons';
import { getTournamentsForSeason, TournamentInfo } from '../tournaments';
import { fetchTournamentDetails } from '../utils/tournamentScraper';

const keyDates = [
  { date: 'Sep 11-13', event: "CCM Howies Hometown Cup", location: "Grand Rapids, MI", path: '/tournaments/howies-hometown-cup-2026' },
  { date: 'Sep 18', event: 'Start of regular CSDHL season', location: '' },
  { date: 'Nov 6-8', event: 'CCM World Invite', location: 'Chicago, IL', path: '/tournaments/ccm-world-invite-2026' },
  { date: 'Nov 27-29', event: 'Silver Sticks', location: 'Chicago, IL', path: '/tournaments/silver-sticks-2026' },
  { date: 'Jan 8-10', event: 'Super Series', location: 'Pittsburgh, PA', path: '/tournaments/super-series-2027' },
  { date: 'Jan 16-18', event: 'CCM MLK Mayhem', location: 'Chicago, IL', path: '/tournaments/ccm-mlk-mayhem-2027' },
  { date: 'Jan 30', event: 'End of Regular Season', location: '' },
  { date: 'Feb 4-7', event: 'CSDHL Playoffs', location: 'Chicago, IL' },
  { date: 'Feb 1-Mar 8', event: 'AHAI State Tournament', location: 'Chicago, IL' },
];

interface EventCalendarProps {
    events: CalendarEvent[];
    filters?: Record<string, boolean>;
    onEventClick: (event: React.MouseEvent<HTMLElement>, calendarEvent: CalendarEvent) => void;
}

const EventCalendar: React.FC<EventCalendarProps> = ({ events, filters, onEventClick }) => {
    const [viewDate, setViewDate] = useState(new Date());
    const today = new Date();
    const todayDate = today.getDate();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();

    const handlePrevMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const handleNextMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    const handleToday = () => setViewDate(new Date());

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDayOfWeek = firstDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const endDayOfWeek = lastDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)

    // Build the grid weeks (including leading days from prev month and trailing days from next month)
    const weeks = useMemo(() => {
        const gridStartDate = new Date(year, month, 1 - startDayOfWeek);
        const gridEndDate = new Date(year, month, daysInMonth + (6 - endDayOfWeek));

        const resultWeeks: Array<Array<{
            date: Date;
            dateStr: string;
            dayNumber: number;
            isCurrentMonth: boolean;
            isToday: boolean;
        }>> = [];

        let curr = new Date(gridStartDate);
        while (curr <= gridEndDate) {
            const week = [];
            for (let d = 0; d < 7; d++) {
                const dYear = curr.getFullYear();
                const dMonth = curr.getMonth();
                const dDate = curr.getDate();
                const dateStr = getLocalDateString(curr);
                const isCurrentMonth = dMonth === month && dYear === year;
                const isToday = dDate === todayDate && dMonth === todayMonth && dYear === todayYear;

                week.push({
                    date: new Date(curr),
                    dateStr,
                    dayNumber: dDate,
                    isCurrentMonth,
                    isToday,
                });
                curr.setDate(curr.getDate() + 1);
            }
            resultWeeks.push(week);
        }

        return resultWeeks;
    }, [year, month, startDayOfWeek, daysInMonth, endDayOfWeek, todayDate, todayMonth, todayYear]);

    // Separate multi-day events and single-day events
    const { multiDayEvents, singleDayEventsByDate } = useMemo(() => {
        const multi: CalendarEvent[] = [];
        const single: Record<string, CalendarEvent[]> = {};

        events.forEach(event => {
            const eventType = getEventType(event);
            if (filters && filters[eventType] === false) {
                return;
            }

            if (isMultiDayEvent(event)) {
                multi.push(event);
            } else {
                const dateStr = getLocalDateString(event.start);
                if (!single[dateStr]) {
                    single[dateStr] = [];
                }
                single[dateStr].push(event);
            }
        });

        // Sort single day events by start time
        Object.keys(single).forEach(dateStr => {
            single[dateStr].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
        });

        return { multiDayEvents: multi, singleDayEventsByDate: single };
    }, [events, filters]);

    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
        <Box
            key={day}
            sx={{
                textAlign: 'center',
                fontWeight: 700,
                py: 1,
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                textTransform: 'uppercase',
                letterSpacing: 0.5,
            }}
        >
            {day}
        </Box>
    ));

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1, sm: 2 }, mb: 4, overflow: 'hidden' }}>
            {/* Calendar Controls Header */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                        {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TodayIcon />}
                        onClick={handleToday}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Today
                    </Button>
                    <IconButton onClick={handlePrevMonth} aria-label="previous month" size="small">
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={handleNextMonth} aria-label="next month" size="small">
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            {/* Day of Week Headers */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    borderTop: '1px solid #e0e0e0',
                    borderLeft: '1px solid #e0e0e0',
                    borderRight: '1px solid #e0e0e0',
                    bgcolor: 'grey.50',
                }}
            >
                {dayHeaders}
            </Box>

            {/* Weeks and Days Grid */}
            <Box
                sx={{
                    borderLeft: '1px solid #e0e0e0',
                    borderRight: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                    bgcolor: 'background.paper',
                }}
            >
                {weeks.map((week, weekIdx) => {
                    const weekStartStr = week[0].dateStr;
                    const weekEndStr = week[6].dateStr;

                    // Multi-day events in this week
                    const activeMultiDayEvents = multiDayEvents.filter(event => {
                        const eventStartStr = getLocalDateString(event.start);
                        const eventEndStr = getLocalDateString(event.end);
                        return eventStartStr <= weekEndStr && eventEndStr >= weekStartStr;
                    });

                    // Sort: earliest start first, then longest duration first
                    activeMultiDayEvents.sort((a, b) => {
                        const aStart = getLocalDateString(a.start);
                        const bStart = getLocalDateString(b.start);
                        if (aStart !== bStart) return aStart.localeCompare(bStart);
                        const aDur = new Date(a.end).getTime() - new Date(a.start).getTime();
                        const bDur = new Date(b.end).getTime() - new Date(b.start).getTime();
                        return bDur - aDur;
                    });

                    // Pack into tracks/slots without overlaps
                    const slots: boolean[][] = [];
                    const positionedMultiEvents: Array<{
                        event: CalendarEvent;
                        startCol: number;
                        endCol: number;
                        span: number;
                        slotIndex: number;
                        isStart: boolean;
                        isEnd: boolean;
                    }> = [];

                    activeMultiDayEvents.forEach(event => {
                        const eventStartStr = getLocalDateString(event.start);
                        const eventEndStr = getLocalDateString(event.end);

                        let startCol = 0;
                        while (startCol < 7 && week[startCol].dateStr < eventStartStr) {
                            startCol++;
                        }
                        let endCol = 6;
                        while (endCol >= 0 && week[endCol].dateStr > eventEndStr) {
                            endCol--;
                        }

                        if (startCol > endCol) return;

                        const isStart = eventStartStr >= weekStartStr;
                        const isEnd = eventEndStr <= weekEndStr;
                        const span = endCol - startCol + 1;

                        // Find first open slot
                        let assignedSlot = -1;
                        for (let s = 0; s < slots.length; s++) {
                            let fits = true;
                            for (let c = startCol; c <= endCol; c++) {
                                if (slots[s][c]) {
                                    fits = false;
                                    break;
                                }
                            }
                            if (fits) {
                                assignedSlot = s;
                                break;
                            }
                        }

                        if (assignedSlot === -1) {
                            assignedSlot = slots.length;
                            slots.push(new Array(7).fill(false));
                        }

                        for (let c = startCol; c <= endCol; c++) {
                            slots[assignedSlot][c] = true;
                        }

                        positionedMultiEvents.push({
                            event,
                            startCol,
                            endCol,
                            span,
                            slotIndex: assignedSlot,
                            isStart,
                            isEnd,
                        });
                    });

                    return (
                        <Box
                            key={`week-${weekIdx}`}
                            sx={{
                                position: 'relative',
                                minHeight: { xs: 110, sm: 130 },
                                borderTop: '1px solid #e0e0e0',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {/* Layer 1: Background Day Cells & Numbers */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    pointerEvents: 'none',
                                }}
                            >
                                {week.map((day, dIdx) => (
                                    <Box
                                        key={day.dateStr}
                                        sx={{
                                            borderRight: dIdx < 6 ? '1px solid #e0e0e0' : 'none',
                                            bgcolor: !day.isCurrentMonth
                                                ? '#f9fafb'
                                                : day.isToday
                                                ? 'rgba(25, 118, 210, 0.04)'
                                                : 'transparent',
                                            p: 0.5,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: day.isToday ? 700 : 500,
                                                color: !day.isCurrentMonth
                                                    ? 'text.disabled'
                                                    : day.isToday
                                                    ? 'primary.contrastText'
                                                    : 'text.primary',
                                                ...(day.isToday && {
                                                    bgcolor: 'primary.main',
                                                    borderRadius: '50%',
                                                    width: 22,
                                                    height: 22,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    lineHeight: 1,
                                                    boxShadow: 1,
                                                }),
                                            }}
                                        >
                                            {day.dayNumber}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {/* Layer 2: Foreground Content (Multi-day Bars + Single-day Events) */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    pt: '26px',
                                    pb: 0.75,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1,
                                    minWidth: 0,
                                    width: '100%',
                                }}
                            >
                                {/* Multi-day Event Banners (Google Calendar Spanning Bars) */}
                                {positionedMultiEvents.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                                            gridTemplateRows: `repeat(${slots.length}, 24px)`,
                                            rowGap: '3px',
                                            mb: 0.75,
                                            minWidth: 0,
                                            width: '100%',
                                        }}
                                    >
                                        {positionedMultiEvents.map(({ event, startCol, endCol, slotIndex, isStart, isEnd }, pIdx) => {
                                            const isPast = new Date(event.end) < new Date();
                                            const eventType = getEventType(event);
                                            const isTournament = eventType === EventType.Tournament;
                                            const startTime = new Date(event.start).getTime();
                                            const endTime = event.end ? new Date(event.end).getTime() : startTime;
                                            const durationHours = (endTime - startTime) / (1000 * 60 * 60);
                                            const isLongOrAllDay = !event.isHoliday && (isTournament || isAllDayEvent(event) || durationHours >= 2.5);

                                            // Determine theme colors for the bar
                                            let barBg = '#1976d2';
                                            let barHoverBg = '#1565c0';
                                            let textColor = '#ffffff';

                                            if (isPast) {
                                                barBg = '#78909c';
                                                barHoverBg = '#607d8b';
                                            } else if (event.color) {
                                                barBg = event.color;
                                                barHoverBg = event.color;
                                            } else if (isLongOrAllDay) {
                                                barBg = '#0284c7';
                                                barHoverBg = '#0369a1';
                                            }

                                            return (
                                                <Box
                                                    key={`${event.summary}-${weekIdx}-${pIdx}`}
                                                    onClick={(e) => onEventClick(e, event)}
                                                    sx={{
                                                        gridColumn: `${startCol + 1} / ${endCol + 2}`,
                                                        gridRow: `${slotIndex + 1} / ${slotIndex + 2}`,
                                                        bgcolor: barBg,
                                                        color: textColor,
                                                        height: 24,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 1,
                                                        cursor: 'pointer',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        minWidth: 0,
                                                        boxSizing: 'border-box',
                                                        transition: 'all 0.15s ease-in-out',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                                                        '&:hover': {
                                                            bgcolor: barHoverBg,
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                            transform: 'translateY(-0.5px)',
                                                        },
                                                        marginLeft: isStart ? '3px' : '0px',
                                                        marginRight: isEnd ? '3px' : '0px',
                                                        borderTopLeftRadius: isStart ? '4px' : '0px',
                                                        borderBottomLeftRadius: isStart ? '4px' : '0px',
                                                        borderTopRightRadius: isEnd ? '4px' : '0px',
                                                        borderBottomRightRadius: isEnd ? '4px' : '0px',
                                                    }}
                                                    title={event.summary}
                                                >
                                                    {isTournament ? (
                                                        <EmojiEventsIcon sx={{ fontSize: '14px', mr: 0.5, flexShrink: 0 }} />
                                                    ) : (
                                                        getEventIcon(event, { fontSize: '14px', mr: 0.5 })
                                                    )}
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontWeight: 600,
                                                            lineHeight: 1.2,
                                                            color: 'inherit',
                                                            fontSize: '0.72rem',
                                                            minWidth: 0,
                                                            flexGrow: 1,
                                                            flexShrink: 1,
                                                        }}
                                                    >
                                                        {getShortenedEventTitle(event.summary)}
                                                        {!isStart && ' (cont.)'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                {/* Single-day Events within Day Columns */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                                        flexGrow: 1,
                                        alignItems: 'start',
                                        minWidth: 0,
                                        width: '100%',
                                    }}
                                >
                                    {week.map((day) => {
                                        const dayEvents = singleDayEventsByDate[day.dateStr] || [];
                                        return (
                                            <Box key={day.dateStr} sx={{ px: 0.5, minWidth: 0, overflow: 'hidden' }}>
                                                <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
                                                    {dayEvents.map((event, i) => {
                                                        const isPast = new Date(event.end) < new Date();
                                                        const eventType = getEventType(event);
                                                        const isGame = eventType === EventType.Game || eventType === EventType.Exhibition || eventType === EventType.Tournament;
                                                        const isHome = isHomeGame(event);
                                                        const isAway = isAwayGame(event);
                                                        const isBirthday = eventType === EventType.Birthday || event.isBirthday;
                                                        const isAllDay = isAllDayEvent(event);
                                                        const startTime = new Date(event.start).getTime();
                                                        const endTime = event.end ? new Date(event.end).getTime() : startTime;
                                                        const durationHours = (endTime - startTime) / (1000 * 60 * 60);
                                                        const isLongOrAllDay = !event.isHoliday && !isBirthday && (isAllDay || durationHours >= 2.5);

                                                        const eventTimeStr = !isAllDay ? new Date(event.start).toLocaleTimeString('en-US', {
                                                            hour: 'numeric',
                                                            minute: '2-digit',
                                                            hour12: true,
                                                            timeZone: 'America/Chicago',
                                                        }).replace(':00', '').toLowerCase() : '';

                                                        let itemBg = '#f3e8ff';
                                                        let itemColor = '#6b21a8';
                                                        let itemBorder = '1px solid #e9d5ff';

                                                        if (isPast) {
                                                            itemBg = 'grey.200';
                                                            itemColor = 'text.secondary';
                                                            itemBorder = '1px solid #e0e0e0';
                                                        } else if (event.isHoliday) {
                                                            itemBg = event.color ? `${event.color}15` : '#fff3e0';
                                                            itemColor = event.color || '#e65100';
                                                            itemBorder = event.color ? `1px solid ${event.color}60` : '1px solid #ffb74d';
                                                        } else if (isBirthday) {
                                                            itemBg = '#fdf2f8';
                                                            itemColor = '#be185d';
                                                            itemBorder = '1px solid #fbcfe8';
                                                        } else if (eventType === EventType.ParentMeeting) {
                                                            itemBg = '#fef3c7';
                                                            itemColor = '#92400e';
                                                            itemBorder = '1px solid #fde68a';
                                                        } else if (eventType === EventType.StickAndPuck) {
                                                            itemBg = '#ccfbf1';
                                                            itemColor = '#0f766e';
                                                            itemBorder = '1px solid #99f6e4';
                                                        } else if (eventType === EventType.SpecialEvent) {
                                                            itemBg = '#fce7f3';
                                                            itemColor = '#9d174d';
                                                            itemBorder = '1px solid #fbcfe8';
                                                        } else if (isLongOrAllDay) {
                                                            itemBg = '#0284c7';
                                                            itemColor = '#ffffff';
                                                            itemBorder = '1px solid #0369a1';
                                                        } else if (isGame) {
                                                            if (isHome) {
                                                                // Home game: richer/darker blue with solid outline all the way around
                                                                itemBg = '#dbeafe';
                                                                itemColor = '#1e3a8a';
                                                                itemBorder = '1.5px solid #2563eb';
                                                            } else if (isAway) {
                                                                // Away game: lighter ice blue with dashed outline all the way around
                                                                itemBg = '#f0f7ff';
                                                                itemColor = '#1d4ed8';
                                                                itemBorder = '1.5px dashed #2563eb';
                                                            } else {
                                                                // Neutral / unspecified game
                                                                itemBg = '#e3f2fd';
                                                                itemColor = '#0d47a1';
                                                                itemBorder = '1.5px solid #3b82f6';
                                                            }
                                                        }

                                                        return (
                                                            <Box
                                                                key={i}
                                                                onClick={(e) => onEventClick(e, event)}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    fontSize: '0.72rem',
                                                                    minWidth: 0,
                                                                    width: '100%',
                                                                    maxWidth: '100%',
                                                                    boxSizing: 'border-box',
                                                                    bgcolor: itemBg,
                                                                    color: itemColor,
                                                                    border: itemBorder,
                                                                    borderRadius: 1,
                                                                    px: 0.5,
                                                                    py: 0.25,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.15s ease-in-out',
                                                                    '&:hover': {
                                                                        filter: 'brightness(0.95)',
                                                                        boxShadow: 1,
                                                                    },
                                                                    ...(event.isHoliday && {
                                                                        fontWeight: 600,
                                                                    }),
                                                                }}
                                                                title={event.summary}
                                                            >
                                                                {getEventIcon(event, { fontSize: '0.85rem', mr: 0.25, color: isLongOrAllDay && !isPast ? '#ffffff' : (isBirthday && !isPast ? '#ec4899' : undefined) })}
                                                                {eventTimeStr && (
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontSize: '0.68rem',
                                                                            fontWeight: 700,
                                                                            flexShrink: 0,
                                                                            lineHeight: 1,
                                                                            color: 'inherit',
                                                                        }}
                                                                    >
                                                                        {eventTimeStr}
                                                                    </Typography>
                                                                )}
                                                                <Typography
                                                                    variant="caption"
                                                                    noWrap
                                                                    sx={{
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: 0,
                                                                        flexGrow: 1,
                                                                        flexShrink: 1,
                                                                        lineHeight: 1.2,
                                                                        fontWeight: (event.isHoliday || isLongOrAllDay || (isGame && isHome)) ? 600 : 500,
                                                                        fontSize: '0.7rem',
                                                                        color: 'inherit',
                                                                    }}
                                                                >
                                                                    {getShortenedEventTitle(event.summary)}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

interface OutletContextType {
    user: User | null;
    isTeamManager: boolean;
}

const STORAGE_KEY = 'calendar_filters';

const DEFAULT_FILTERS: Record<string, boolean> = {
    [EventType.Game]: true,
    [EventType.Exhibition]: true,
    [EventType.Tournament]: true,
    [EventType.SpecialEvent]: true,
    [EventType.ParentMeeting]: true,
    [EventType.StickAndPuck]: true,
    [EventType.Practice]: true,
    [EventType.GVN]: true,
    [EventType.Floorball]: true,
    [EventType.Goalie]: true,
    [EventType.Holiday]: true,
    [EventType.Birthday]: true,
    [EventType.Other]: true,
};

const getInitialFilters = (): Record<string, boolean> => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (typeof parsed === 'object' && parsed !== null) {
                return {
                    ...DEFAULT_FILTERS,
                    ...parsed,
                };
            }
        }
    } catch (e) {
        console.warn('Failed to load calendar filters from localStorage:', e);
    }
    return DEFAULT_FILTERS;
};

const CalendarPage = () => {
    const [baseEvents, setBaseEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<Record<string, boolean>>(getInitialFilters);
    const { isTeamManager } = useOutletContext<OutletContextType>();
    const { rosters, getRoster, isAuthReady } = useRoster();

    const currentSeasonRoster = getRoster(CURRENT_SEASON_KEY) || getRoster('pw_26_27') || (Object.values(rosters)[0] || null);

    const birthdayEvents = useMemo(() => {
        return getPlayerBirthdayEvents(currentSeasonRoster);
    }, [currentSeasonRoster]);

    const allEvents = useMemo(() => {
        const existingBirthdayKeys = new Set(
            baseEvents
                .filter(e => e.isBirthday)
                .map(e => `${(e.summary || '').trim().toLowerCase()}-${(e.start || '').slice(0, 10)}`)
        );
        const dedupedBirthdays = birthdayEvents.filter(
            b => !existingBirthdayKeys.has(`${(b.summary || '').trim().toLowerCase()}-${(b.start || '').slice(0, 10)}`)
        );
        const combined = [...baseEvents, ...dedupedBirthdays];
        return combined.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    }, [baseEvents, birthdayEvents]);

    const upcomingEvents = useMemo(() => {
        const cutoffTime = new Date();
        cutoffTime.setMinutes(cutoffTime.getMinutes() - 30);
        return allEvents.filter((event: CalendarEvent) => {
            const eventEnd = event.end ? new Date(event.end) : new Date(event.start);
            return eventEnd > cutoffTime;
        });
    }, [allEvents]);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

    const isSelectedEventGame = selectedEvent &&
        (getEventType(selectedEvent) === EventType.Game || getEventType(selectedEvent) === EventType.Exhibition || getEventType(selectedEvent) === EventType.Tournament);

    const handleEventClick = (event: React.MouseEvent<HTMLElement>, calendarEvent: CalendarEvent) => {
        setAnchorEl(event.currentTarget);
        setSelectedEvent(calendarEvent);
        logEvent('view_calendar_event_details', { event_summary: calendarEvent.summary });
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setSelectedEvent(null);
    };

    const filterConfig: { label: string, type: EventType }[] = [
        { label: 'Regular Season Games', type: EventType.Game },
        { label: 'Exhibition Games', type: EventType.Exhibition },
        { label: 'Tournaments', type: EventType.Tournament },
        { label: 'Special Events', type: EventType.SpecialEvent },
        { label: 'Parent Meetings', type: EventType.ParentMeeting },
        { label: 'Stick & Puck', type: EventType.StickAndPuck },
        { label: 'Practices', type: EventType.Practice },
        { label: 'GVN', type: EventType.GVN },
        { label: 'Floorball Events', type: EventType.Floorball },
        { label: 'Goalie Events', type: EventType.Goalie },
        { label: 'Holidays', type: EventType.Holiday },
        { label: 'Birthdays', type: EventType.Birthday },
        { label: 'Unknown Events', type: EventType.Other },
    ];

    useEffect(() => {
        document.title = "Jets '14 | Calendar";
        if (!isAuthReady) return;

        let isMounted = true;
        const fetchAllCalendarData = async () => {
            try {
                const mergedEvents = await fetchAndMergeCalendarData();
                if (isMounted) {
                    setBaseEvents(mergedEvents);
                }
            } catch (e: any) {
                if (isMounted) {
                    setError(e.message);
                    console.error("Failed to fetch calendar events:", e);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchAllCalendarData();

        return () => {
            isMounted = false;
        };
    }, [isAuthReady]);

    // Fetch tournament details in background to merge tournament games into calendar events
    useEffect(() => {
        if (!isAuthReady) return;

        const seasonTournaments = getTournamentsForSeason(CURRENT_SEASON_KEY).filter(t => Boolean(t.scheduleUrl));
        if (seasonTournaments.length === 0) return;

        let isMounted = true;
        Promise.all(
            seasonTournaments.map(async target => {
                try {
                    const data = await fetchTournamentDetails(target);
                    if (!data || !data.games || data.games.length === 0) return null;
                    const jetsGames = data.games.filter(g =>
                        (g.homeTeam || '').toLowerCase().includes('jets') ||
                        (g.visitorTeam || '').toLowerCase().includes('jets')
                    );
                    if (jetsGames.length === 0) return null;
                    const jetsCalEvents = jetsGames.map(g => convertTournamentGameToCalendarEvent(g, target));
                    return { target, jetsCalEvents };
                } catch (err) {
                    console.warn(`Error fetching tournament details for ${target.name} in CalendarPage:`, err);
                    return null;
                }
            })
        ).then(results => {
            if (!isMounted) return;
            const validResults = results.filter((r): r is { target: TournamentInfo; jetsCalEvents: CalendarEvent[] } => r !== null);
            if (validResults.length > 0) {
                setBaseEvents(prevEvents => {
                    let updated = prevEvents;
                    for (const { target, jetsCalEvents } of validResults) {
                        updated = mergeTournamentAndCalendarEvents(updated, jetsCalEvents, target, { keepMultiDayPlaceholder: true });
                    }
                    return updated;
                });
            }
        });

        return () => {
            isMounted = false;
        };
    }, [isAuthReady]);

    const handleFilterToggle = (type: EventType) => {
        const newEnabledState = !filters[type];
        const newFilters = { ...filters, [type]: newEnabledState };
        setFilters(newFilters);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
        } catch (e) {
            console.warn('Failed to save calendar filters to localStorage:', e);
        }
        logEvent('filter_calendar_events', {
            event_type: EventType[type],
            enabled: newEnabledState,
        });
    };

    const filteredEvents = upcomingEvents.filter(event => {
        const type = getEventType(event);
        return filters[type];
    });

    return (
        <Container maxWidth="xl">
            <Box sx={{ my: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Calendar
                </Typography>
                {isTeamManager && <ScheduleConflictDisplay calendarEvents={baseEvents} isLoadingEvents={loading} />}

                <EventCalendar events={allEvents} filters={filters} onEventClick={handleEventClick} />

                <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 4 }}>
                    Upcoming Events
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 2, flexWrap: 'wrap' }}>
                    {filterConfig.map(({ label, type }) => (
                        <Chip
                            key={type}
                            label={label}
                            onClick={() => handleFilterToggle(type)}
                            color={filters[type] ? 'primary' : 'default'}
                            variant={filters[type] ? 'filled' : 'outlined'}
                        />
                    ))}
                </Stack>
                <Paper variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
                    <TableContainer>
                        <Table aria-label="upcoming events table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date & Time</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Watch / Info</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from(new Array(5)).map((_, index) => (
                                        <TableRow key={index}>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton /></TableCell>
                                        </TableRow>
                                    ))
                                ) : error ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Alert severity="error">Could not load calendar events: {error}</Alert>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredEvents.map((event, index) => {
                                        const isMultiDay = isMultiDayEvent(event);
                                        const eventType = getEventType(event);
                                        const isGame = eventType === EventType.Game || eventType === EventType.Exhibition || eventType === EventType.Tournament;
                                        const isTournament = eventType === EventType.Tournament;

                                        return (
                                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell sx={{ fontWeight: 'medium', width: '35%', verticalAlign: 'top' }}>
                                                    <Stack>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            {getEventIcon(event)}
                                                            {event.url ? (
                                                                event.url.startsWith('/') ? (
                                                                    <Link
                                                                        component={RouterLink}
                                                                        to={event.url}
                                                                        onClick={() => logEvent('click_event_link', { event_summary: event.summary, url: event.url })}
                                                                    >
                                                                        {event.summary}
                                                                    </Link>
                                                                ) : (
                                                                    <Link
                                                                        href={event.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={() => logEvent('click_event_link', { event_summary: event.summary, url: event.url })}
                                                                    >
                                                                        {event.summary}
                                                                    </Link>
                                                                )
                                                            ) : (
                                                                <Typography variant="body2">{event.summary}</Typography>
                                                            )}
                                                        </Stack>
                                                        {event.isRollover && !event.isHoliday && (
                                                            <Box
                                                                onClick={(e) => handleEventClick(e, event)}
                                                                sx={{
                                                                    pl: 3.5, pt: 0.5, cursor: 'pointer',
                                                                    '&:hover': { opacity: 0.8 }
                                                                }}
                                                            >
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <InfoOutlinedIcon sx={{ fontSize: '1rem' }} color="action" />
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                        Rollover practice
                                                                    </Typography>
                                                                </Stack>
                                                            </Box>
                                                        )}
                                                        {event.duties && Object.keys(event.duties).length > 0 && !event.isHoliday && (
                                                            <Box sx={{ pl: 3.5, pt: 0.5 }}>
                                                                {Object.entries(event.duties).map(([duty, name]) => (
                                                                    <Typography key={duty} variant="caption" color="text.secondary" display="block">
                                                                        <strong>{duty}:</strong> {name}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </TableCell>

                                                <TableCell sx={{ width: '25%' }}>
                                                    <Typography variant="body2">{formatEventDateRange(event)}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {isMultiDay ? 'Multi-Day Event' : isAllDayEvent(event) ? 'All Day' : formatEventTime(event)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ width: '25%' }}>
                                                    {event.location && event.location !== 'Holiday' && event.location !== 'Birthday' ? formatLocation(event.location, event) : ''}
                                                </TableCell>

                                                <TableCell sx={{ width: '15%' }}>
                                                    {isGame && !isMultiDay && getLiveBarnLink(event.location, event.start)}
                                                    {isTournament && event.url && (
                                                        event.url.startsWith('/') ? (
                                                            <Link component={RouterLink} to={event.url} variant="body2">
                                                                Tournament Info
                                                            </Link>
                                                        ) : (
                                                            <Link href={event.url} target="_blank" rel="noopener noreferrer" variant="body2">
                                                                Tournament Site
                                                            </Link>
                                                        )
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Event Detail Popover */}
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ sx: { p: 2, maxWidth: 350, border: 1, borderColor: 'grey.300', borderRadius: 2, boxShadow: 3 } }}
                >
                    {selectedEvent && (
                        <>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {getEventIcon(selectedEvent)}
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{selectedEvent.summary}</Typography>
                            </Stack>
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" spacing={1} alignItems="center">
                                <AccessTimeIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {isMultiDayEvent(selectedEvent) ? (
                                        `${formatEventDateRange(selectedEvent)} (Multi-Day)`
                                    ) : isAllDayEvent(selectedEvent) ? (
                                        `${new Date(selectedEvent.start).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Chicago' })} (All Day)`
                                    ) : (
                                        `${new Date(selectedEvent.start).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Chicago' })} • ${formatEventTime(selectedEvent, 'start')} - ${formatEventTime(selectedEvent, 'end')}`
                                    )}
                                </Typography>
                            </Stack>
                            {selectedEvent.location && selectedEvent.location !== 'Holiday' && selectedEvent.location !== 'Birthday' && (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <LocationOnIcon fontSize="small" color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                        {formatLocation(selectedEvent.location, selectedEvent)}
                                    </Typography>
                                </Stack>
                            )}

                            {selectedEvent.url && (
                                <Box sx={{ mt: 1.5 }}>
                                    {selectedEvent.url.startsWith('/') ? (
                                        <Button
                                            component={RouterLink}
                                            to={selectedEvent.url}
                                            variant="contained"
                                            size="small"
                                            fullWidth
                                            onClick={() => {
                                                handlePopoverClose();
                                                logEvent('click_tournament_link', { summary: selectedEvent.summary, url: selectedEvent.url });
                                            }}
                                        >
                                            View Tournament Details
                                        </Button>
                                    ) : (
                                        <Button
                                            href={selectedEvent.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            onClick={() => {
                                                logEvent('click_event_external_url', { summary: selectedEvent.summary, url: selectedEvent.url });
                                            }}
                                        >
                                            Visit Tournament Website
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {selectedEvent.isRollover && (
                                <Box sx={{ mt: 1.5, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <InfoOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                                        <Typography variant="caption" color="text.secondary">
                                            This is a rollover practice. The original event title is: "
                                            <Typography variant="caption" component="span" sx={{ fontStyle: 'italic' }}>{selectedEvent.originalSummary}</Typography>
                                            ". The full practice time is {new Date(selectedEvent.originalStart!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' })}
                                            {' - '}
                                            {new Date(selectedEvent.originalEnd!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' })}.
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}
                            {selectedEvent.duties && Object.keys(selectedEvent.duties).length > 0 && (
                                <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Volunteers:</Typography>
                                    <Stack component="ul" sx={{ m: 0, p: 0, pl: 2 }}>
                                        {Object.entries(selectedEvent.duties).map(([duty, name]) => (
                                            <Typography component="li" key={duty} variant="caption" color="text.secondary" sx={{ listStyleType: 'none' }}>
                                                <strong>{duty}:</strong> {name}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            {isSelectedEventGame && !isMultiDayEvent(selectedEvent) && (
                                <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                                    {getLiveBarnLink(selectedEvent.location, selectedEvent.start)}
                                </Box>
                            )}
                        </>
                    )}
                </Popover>

                <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 4 }}>
                    Season Key Dates
                </Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                    <TableContainer>
                        <Table aria-label="season key dates table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {keyDates.map((item, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell sx={{ fontWeight: 'medium', width: '40%' }}>
                                            {item.path ? (
                                                <Link component={RouterLink} to={item.path} onClick={() => logEvent('click_key_date_link', { event_name: item.event, path: item.path })}>
                                                    {item.event}
                                                </Link>
                                            ) : (
                                                item.event
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ width: '30%' }}>{item.date}</TableCell>
                                        <TableCell sx={{ width: '30%' }}>{formatLocation(item.location)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Container>
    );
};

export default CalendarPage;